import * as React from "react";
import { format } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ColumnDef, ExpandedState, Row } from "@tanstack/react-table";
import { AxiosError } from "axios";
import {
    Alert,
    Button,
    Card,
    Checkbox,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../../components/Navbar/Navbar";
import ListState from "../../../components/ListState/ListState";
import ArrowUpRightIcon from "../../../icons/ArrowUpRight";
import ChevronDownIcon from "../../../icons/ChevronDown";
import ChevronUpIcon from "../../../icons/ChevronUp";
import { useDebounce } from "../../../hooks/helpers";
import { useFetchInsuranceProviders } from "../../../hooks/queries/client";
import useToast from "../../../hooks/useToast";
import { fetchNotesList } from "../../../services/note";
import Loader from "../../../shared-ui/Loader/Loader";
import TableFiltersDisplay from "../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import InsuranceProviderFilter from "../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";
import { LISTS_DEFAULT_LIMIT } from "../../../utils/constants";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    removeEnumUnderscore,
} from "../../../utils/helpers";
import {
    IApiResponse,
    IPaginatedApiResponse,
} from "../../../utils/types/api-response";
import { IClientInsuranceProvider } from "../../../utils/types/client";
import { useCreateBulkParentClaim } from "../../claims/hooks/claims.mutations";
import { isPsychNote } from "../helpers/notes.helpers";
import { INote, NoteLabels, NoteStatus, NoteTypes } from "../types/notes.types";

interface GroupedNotes {
    clientId: string;
    clientName: string;
    insuranceLabel: string;
    notes: INote[];
}

interface ClientTableRow {
    clientId: string;
    group: GroupedNotes;
    selectAllControl: React.ReactNode;
    clientName: string;
    insuranceLabel: string;
    fromDos: string;
    toDos: string;
    notesCount: number;
    selectedCount: number;
    actionControl: React.ReactNode;
}

interface NoteTableRow {
    noteId: string;
    selectControl: React.ReactNode;
    dateOfService: string;
    noteType: string;
    cptCode: React.ReactNode;
    insurance: string;
    clinician: string;
    createdAt: string;
    actionControl: React.ReactNode;
}

interface ISubComponentNotes {
    // eslint-disable-next-line react/no-unused-prop-types
    row: Row<ClientTableRow>;
}

const INVALID_CPT_CODES = new Set(["ROD", "--"]);
const COMPACT_TABLE_CLASS = "[&_th]:!py-6 [&_td]:!py-6";
const NOTES_FETCH_PAGE_SIZE = 200;
const SELF_PAY_FILTER_ID = "self_pay";

const clientColumns: ColumnDef<ClientTableRow>[] = [
    {
        id: "expandControl",
        header: "",
        cell: ({ row }) => (
            <Button
                size="auto"
                variant="normal"
                onClick={(e) => {
                    e.stopPropagation();
                    row.toggleExpanded();
                    row.getToggleExpandedHandler();
                }}
            >
                {row.getIsExpanded() ? (
                    <ChevronUpIcon className="size-4" />
                ) : (
                    <ChevronDownIcon className="size-4" />
                )}
            </Button>
        ),
    },
    {
        id: "selectAllControl",
        header: "SELECT ALL",
        cell: ({ row }) => row.original.selectAllControl,
    },
    {
        accessorKey: "clientName",
        header: "CLIENT",
    },
    {
        accessorKey: "insuranceLabel",
        header: "INSURANCE",
    },
    {
        accessorKey: "fromDos",
        header: "FROM DOS",
    },
    {
        accessorKey: "toDos",
        header: "TO DOS",
    },
    {
        accessorKey: "notesCount",
        header: "NOTES",
    },
    {
        accessorKey: "selectedCount",
        header: "SELECTED",
    },
    {
        id: "actionControl",
        header: "ACTION",
        cell: ({ row }) => row.original.actionControl,
    },
];

const noteColumns: ColumnDef<NoteTableRow>[] = [
    {
        id: "selectControl",
        header: "SELECT",
        cell: ({ row }) => row.original.selectControl,
    },
    {
        accessorKey: "dateOfService",
        header: "DATE OF SERVICE",
    },
    {
        accessorKey: "noteType",
        header: "NOTE TYPE",
    },
    {
        id: "cptCode",
        header: "CPT CODE(S)",
        cell: ({ row }) => row.original.cptCode,
    },
    {
        accessorKey: "insurance",
        header: "INSURANCE",
    },
    {
        accessorKey: "clinician",
        header: "CLINICIAN",
    },
    {
        accessorKey: "createdAt",
        header: "CREATED",
    },
    {
        id: "actionControl",
        header: "ACTION",
        cell: ({ row }) => row.original.actionControl,
    },
];

function getInsuranceSelectionKey(note: INote): string | null {
    if (!note.insurance_provider_id) {
        return null;
    }
    return `insurance:${note.insurance_provider_id}`;
}

function getNoteCptCodeEntries(note: INote): string[] {
    const unitAssignments =
        note.note_metadata?.unit_calculation?.unit_assignments;

    if (unitAssignments && unitAssignments.length > 0) {
        return unitAssignments
            .map((assignment) => assignment.cpt_code?.trim())
            .filter((cptCode): cptCode is string => Boolean(cptCode))
            .filter((cptCode) => {
                const normalized = cptCode.toUpperCase();
                return !INVALID_CPT_CODES.has(normalized);
            });
    }

    const normalizedCptCode = note.cpt_code?.trim().toUpperCase();
    if (!normalizedCptCode || INVALID_CPT_CODES.has(normalizedCptCode)) {
        return [];
    }

    return [note.cpt_code.trim()];
}

function getAllNoteCptCodes(note: INote): React.ReactNode {
    const cptCodes = getNoteCptCodeEntries(note);
    if (cptCodes.length > 0) {
        const cptCodeCountMap = cptCodes.reduce((map, cptCode) => {
            map.set(cptCode, (map.get(cptCode) ?? 0) + 1);
            return map;
        }, new Map<string, number>());

        return (
            <span className="flex flex-wrap items-center gap-2">
                {Array.from(cptCodeCountMap.entries()).map(
                    ([cptCode, count]) => (
                        <span
                            key={cptCode}
                            className="inline-flex items-center gap-1"
                        >
                            <span>{cptCode}</span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                                x {count}
                            </span>
                        </span>
                    )
                )}
            </span>
        );
    }

    return note.cpt_code || "--";
}

function buildNoteUrl(note: INote): string {
    return `/notes/${note.client.client_id}/${note.note_id}?viewFrom=session_note`;
}

function getNoteInsuranceLabel(note: INote): string {
    if (note.client.self_pay) {
        return "Self pay";
    }

    return (
        removeEnumUnderscore(note.client.insurance_provider_name || "") || "--"
    );
}

function getNoteDosSortValue(note: INote): number {
    if (!note.date_of_service) {
        return 0;
    }

    const dosValue = new Date(note.date_of_service).getTime();
    return Number.isNaN(dosValue) ? 0 : dosValue;
}

function formatDosValue(note?: INote): string {
    if (!note?.date_of_service) {
        return "--";
    }

    return formatDate(note.date_of_service);
}

export default function CodedClientNotesPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const [expandedRows, setExpandedRows] = React.useState<ExpandedState>({});
    const [selectedNotesByClient, setSelectedNotesByClient] = React.useState<
        Record<string, string[]>
    >({});
    const [selectionValidationBanner, setSelectionValidationBanner] =
        React.useState<string>("");
    const hasAppliedDefaultInsuranceFilter = React.useRef(false);

    const pageFilter = Number(searchParams.get("page") || 1);
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const searchStringFilter = searchParams.get("search_string") || "";
    const noteTypeFilter = searchParams.getAll("type[]");
    const insuranceProviderIdsFilter = searchParams.getAll(
        "insurance_provider_ids[]"
    );
    const insuranceProviderIdsFilterKey = React.useMemo(
        () => [...insuranceProviderIdsFilter].sort().join(","),
        [insuranceProviderIdsFilter]
    );
    const insuranceProviderIdsFilterSet = React.useMemo(
        () =>
            new Set(
                insuranceProviderIdsFilterKey
                    ? insuranceProviderIdsFilterKey.split(",")
                    : []
            ),
        [insuranceProviderIdsFilterKey]
    );
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const debouncedSearchStringFilter = useDebounce(searchStringFilter, 500);
    const insuranceProviders = useFetchInsuranceProviders();

    const insuranceOptions = React.useMemo(() => {
        const providers = insuranceProviders.data?.data || [];

        return [
            {
                insurance_provider_id: SELF_PAY_FILTER_ID,
                alias: "Self pay",
            } as IClientInsuranceProvider,
            ...providers,
        ].sort((a, b) => a.alias.localeCompare(b.alias));
    }, [insuranceProviders.data?.data]);

    const nonSelfPayInsuranceProviderIds = React.useMemo(
        () =>
            (insuranceProviders.data?.data || []).map(
                (provider) => provider.insurance_provider_id
            ),
        [insuranceProviders.data?.data]
    );
    const {
        data: notesResponse,
        isLoading,
        isSuccess,
        error,
    } = useQuery<
        IPaginatedApiResponse<INote[]>,
        AxiosError<IApiResponse<INote[]>>
    >({
        queryKey: [
            "coded-client-notes",
            debouncedSearchStringFilter,
            noteTypeFilter,
            fromDateFilter,
            toDateFilter,
        ],
        queryFn: async () => {
            const baseFilters = {
                search_string: debouncedSearchStringFilter,
                status: [NoteStatus.CODED],
                type: noteTypeFilter,
                from_date: fromDateFilter,
                to_date: toDateFilter,
                sort_attribute: "date_of_service",
                sort_order: "ASC",
                tab_name: "all_notes" as const,
                show_notes_without_claims: "true" as const,
                show_notes_eligible_for_bulk_claim: "true" as const,
            };

            const firstPage = await fetchNotesList(
                {
                    ...baseFilters,
                    page: 1,
                    limit: NOTES_FETCH_PAGE_SIZE,
                },
                ""
            );

            const combinedData = [...(firstPage.data || [])];
            const totalPages = firstPage.total_pages || 1;
            const remainingPages = Array.from(
                { length: Math.max(totalPages - 1, 0) },
                (_, index) => index + 2
            );

            if (remainingPages.length > 0) {
                const remainingResponses = await Promise.all(
                    remainingPages.map((page) =>
                        fetchNotesList(
                            {
                                ...baseFilters,
                                page,
                                limit: NOTES_FETCH_PAGE_SIZE,
                            },
                            ""
                        )
                    )
                );
                remainingResponses.forEach((pageResponse) => {
                    combinedData.push(...(pageResponse.data || []));
                });
            }

            return {
                ...firstPage,
                data: combinedData,
                count: combinedData.length,
            } as IPaginatedApiResponse<INote[]>;
        },
    });

    const createBulkParentClaim = useCreateBulkParentClaim();

    React.useEffect(() => {
        if (hasAppliedDefaultInsuranceFilter.current) {
            return;
        }

        if (insuranceProviderIdsFilter.length > 0) {
            hasAppliedDefaultInsuranceFilter.current = true;
            return;
        }

        if (nonSelfPayInsuranceProviderIds.length === 0) {
            return;
        }

        const nextSearchParams = new URLSearchParams(searchParams);
        nonSelfPayInsuranceProviderIds.forEach((insuranceProviderId) => {
            nextSearchParams.append(
                "insurance_provider_ids[]",
                insuranceProviderId
            );
        });
        nextSearchParams.set("page", "1");
        hasAppliedDefaultInsuranceFilter.current = true;
        setSearchParams(nextSearchParams);
    }, [
        insuranceProviderIdsFilter.length,
        nonSelfPayInsuranceProviderIds,
        searchParams,
        setSearchParams,
    ]);

    const groupedNotes = React.useMemo(() => {
        const grouped = new Map<string, GroupedNotes>();
        const filteredNotes = (notesResponse?.data || []).filter((note) => {
            if (insuranceProviderIdsFilterSet.size === 0) {
                return true;
            }

            if (note.client.self_pay) {
                return insuranceProviderIdsFilterSet.has(SELF_PAY_FILTER_ID);
            }

            if (!note.insurance_provider_id) {
                return true;
            }

            return insuranceProviderIdsFilterSet.has(
                note.insurance_provider_id
            );
        });

        filteredNotes.forEach((note) => {
            const clientId = note.client.client_id;
            const clientName = displayNameInRightFormat({
                firstName: note.client.first_name,
                lastName: note.client.last_name,
            });

            const insuranceLabel = note.client.self_pay
                ? "Self pay"
                : removeEnumUnderscore(
                      note.client.insurance_provider_name || ""
                  ) || "--";

            if (!grouped.has(clientId)) {
                grouped.set(clientId, {
                    clientId,
                    clientName,
                    insuranceLabel,
                    notes: [note],
                });
            } else {
                grouped.get(clientId)?.notes.push(note);
            }
        });

        const groups = Array.from(grouped.values()).map((group) => ({
            ...group,
            notes: [...group.notes].sort(
                (a, b) => getNoteDosSortValue(a) - getNoteDosSortValue(b)
            ),
        }));

        return groups.sort((a, b) => {
            const aDos = getNoteDosSortValue(a.notes[0]);
            const bDos = getNoteDosSortValue(b.notes[0]);

            if (aDos === bDos) {
                return a.clientName.localeCompare(b.clientName);
            }

            return aDos - bDos;
        });
    }, [insuranceProviderIdsFilterSet, notesResponse?.data]);

    const totalClientCount = groupedNotes.length;
    const totalPages = Math.ceil(totalClientCount / limitFilter) || 1;
    const currentPage = Math.min(pageFilter, totalPages);
    const startIndex = (currentPage - 1) * limitFilter;
    const paginatedGroupedNotes = groupedNotes.slice(
        startIndex,
        startIndex + limitFilter
    );

    React.useEffect(() => {
        setSelectedNotesByClient({});
    }, [groupedNotes]);

    React.useEffect(() => {
        if (pageFilter > totalPages && totalClientCount > 0) {
            searchParams.set("page", String(totalPages));
            setSearchParams(searchParams);
        }
    }, [
        pageFilter,
        totalPages,
        totalClientCount,
        searchParams,
        setSearchParams,
    ]);

    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") {
            searchParams.delete("search_string");
        } else {
            searchParams.set("search_string", e.target.value);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleSetTypeFilter = (noteType: NoteTypes) => {
        if (noteTypeFilter.includes(noteType)) {
            searchParams.delete("type[]", noteType);
        } else {
            searchParams.append("type[]", noteType);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleInsuranceProviderIdsChange = (
        insuranceProviderIds: string[]
    ) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete("insurance_provider_ids[]");
        insuranceProviderIds.forEach((id) =>
            nextSearchParams.append("insurance_provider_ids[]", id)
        );
        nextSearchParams.set("page", "1");
        setSearchParams(nextSearchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) {
            searchParams.delete("from_date");
        } else {
            searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) {
            searchParams.delete("to_date");
        } else {
            searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const getSelectedNoteIds = React.useCallback(
        (clientId: string) => selectedNotesByClient[clientId] || [],
        [selectedNotesByClient]
    );

    const toggleNoteSelection = React.useCallback(
        (clientId: string, noteId: string) => {
            const selectedForClient = getSelectedNoteIds(clientId);

            if (selectedForClient.includes(noteId)) {
                setSelectedNotesByClient({
                    ...selectedNotesByClient,
                    [clientId]: selectedForClient.filter((id) => id !== noteId),
                });
            } else {
                setSelectedNotesByClient({
                    ...selectedNotesByClient,
                    [clientId]: [...selectedForClient, noteId],
                });
            }
        },
        [getSelectedNoteIds, selectedNotesByClient]
    );

    const toggleClientSelection = (group: GroupedNotes) => {
        const selectedForClient = getSelectedNoteIds(group.clientId);

        if (selectedForClient.length === group.notes.length) {
            setSelectedNotesByClient({
                ...selectedNotesByClient,
                [group.clientId]: [],
            });
        } else {
            setSelectedNotesByClient({
                ...selectedNotesByClient,
                [group.clientId]: group.notes.map((note) => note.note_id),
            });
        }
    };

    const getSelectedNotesForClient = (group: GroupedNotes) => {
        const selectedForClient = getSelectedNoteIds(group.clientId);
        return group.notes.filter((note) =>
            selectedForClient.includes(note.note_id)
        );
    };

    const getSelectionValidationMessage = (group: GroupedNotes) => {
        const selectedNotes = getSelectedNotesForClient(group);
        const insuranceSelectionKeys = selectedNotes.map(
            getInsuranceSelectionKey
        );

        if (insuranceSelectionKeys.includes(null)) {
            return "Insurance provider is required for all notes";
        }

        const insuranceProviderSet = new Set(
            insuranceSelectionKeys as string[]
        );

        if (insuranceProviderSet.size > 1) {
            return "All selected notes must use the same insurance provider";
        }

        const noteCategorySet = new Set(
            selectedNotes.map((note) =>
                isPsychNote(note.note_type) ? "testing" : "therapy"
            )
        );

        if (noteCategorySet.size > 1) {
            return "All selected notes must belong to the same category";
        }

        return "";
    };

    const handleGenerateClaim = (group: GroupedNotes) => {
        const selectedNotes = getSelectedNotesForClient(group);
        const validationMessage = getSelectionValidationMessage(group);

        if (selectedNotes.length === 0) {
            return;
        }

        if (validationMessage) {
            setSelectionValidationBanner(validationMessage);
            return;
        }

        setSelectionValidationBanner("");
        createBulkParentClaim.mutate(
            {
                note_ids: selectedNotes.map((note) => note.note_id),
            },
            {
                onSuccess: (res) => {
                    const externalReferenceId = res.data.external_reference_id;
                    const claimsListParams = new URLSearchParams({
                        tab: "submission_ready",
                        status: "ready_for_submission",
                        page: "1",
                    });
                    if (externalReferenceId) {
                        claimsListParams.set("search", externalReferenceId);
                    }
                    navigate(`/claims?${claimsListParams.toString()}`);

                    toast({
                        mode: "success",
                        message: res.message || "Claim created successfully",
                    });

                    queryClient
                        .invalidateQueries({
                            queryKey: ["coded-client-notes"],
                        })
                        .catch(() => {});
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not create claim at this time",
                    });
                },
            }
        );
    };

    const clientTableRows: ClientTableRow[] = paginatedGroupedNotes.map(
        (group) => {
            const selectedIds = getSelectedNoteIds(group.clientId);
            const selectedCount = selectedIds.length;
            const allSelected =
                selectedCount > 0 && selectedCount === group.notes.length;

            return {
                clientId: group.clientId,
                group,
                selectAllControl: (
                    <Checkbox
                        handleChange={() => toggleClientSelection(group)}
                        label=""
                        name={`select-${group.clientId}`}
                        value={group.clientId}
                        isChecked={allSelected}
                    />
                ),
                clientName: group.clientName,
                insuranceLabel: group.insuranceLabel,
                fromDos: formatDosValue(group.notes[0]),
                toDos: formatDosValue(group.notes[group.notes.length - 1]),
                notesCount: group.notes.length,
                selectedCount,
                actionControl: (
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateClaim(group);
                        }}
                        disabled={
                            selectedCount === 0 ||
                            createBulkParentClaim.isLoading
                        }
                    >
                        Generate claim
                    </Button>
                ),
            };
        }
    );

    const getNoteRows = React.useCallback(
        (group: GroupedNotes): NoteTableRow[] => {
            const selectedIds = getSelectedNoteIds(group.clientId);

            return group.notes.map((note) => ({
                noteId: note.note_id,
                selectControl: (
                    <Checkbox
                        handleChange={() =>
                            toggleNoteSelection(group.clientId, note.note_id)
                        }
                        label=""
                        name={`note-${note.note_id}`}
                        value={note.note_id}
                        isChecked={selectedIds.includes(note.note_id)}
                    />
                ),
                dateOfService: note.date_of_service
                    ? formatDate(note.date_of_service)
                    : "--",
                noteType: NoteLabels[note.note_type],
                cptCode: getAllNoteCptCodes(note),
                insurance: getNoteInsuranceLabel(note),
                clinician: displayNameInRightFormat({
                    firstName: note.author.first_name,
                    lastName: note.author.last_name,
                }),
                createdAt: formatDate(note.created_at),
                actionControl: (
                    <Button
                        size="auto"
                        variant="normal"
                        iconRight={<ArrowUpRightIcon className="size-4" />}
                        onClick={() => {
                            window.open(
                                buildNoteUrl(note),
                                "_blank",
                                "noopener,noreferrer"
                            );
                        }}
                    >
                        Open note
                    </Button>
                ),
            }));
        },
        [getSelectedNoteIds, toggleNoteSelection]
    );

    const renderRowSubComponent = React.useCallback(
        ({ row }: ISubComponentNotes) => (
            <div className="py-6">
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-4">
                    <Table
                        data={getNoteRows(row.original.group)}
                        columns={noteColumns}
                        className={COMPACT_TABLE_CLASS}
                    />
                </div>
            </div>
        ),
        [getNoteRows]
    );

    return (
        <>
            <Navbar title="Coded Notes" />
            <div className="page">
                <Alert
                    type="info"
                    description="This list shows coded notes where claims were not automatically created."
                    classNames="mb-[--spacing-items]"
                />
                <Card classNames="mb-[--spacing-items]">
                    <div className="flex flex-col gap-[--spacing-items]">
                        <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                            <SearchInput
                                value={searchStringFilter}
                                onChange={handleSetSearchString}
                                placeholder="Search by client"
                                containerClass="w-[400px] max-w-full"
                            />
                        </div>
                        <div className="flex items-center gap-[--spacing-items] flex-wrap">
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>Note type</FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width="auto">
                                    {Object.values(NoteTypes).map(
                                        (noteType) => (
                                            <DropdownItem
                                                key={noteType}
                                                onClick={() =>
                                                    handleSetTypeFilter(
                                                        noteType
                                                    )
                                                }
                                                itemValue={noteType}
                                                isMulti
                                                values={noteTypeFilter}
                                            >
                                                {NoteLabels[noteType]}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>
                            <InsuranceProviderFilter
                                values={insuranceProviderIdsFilter}
                                onChange={handleInsuranceProviderIdsChange}
                                alwaysShowSelfPay
                                align="start"
                            />
                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date of Service</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent
                                    align="end"
                                    className="flex gap-y-2"
                                >
                                    <div className="mb-1">
                                        <DatePicker
                                            label="From DOS"
                                            placeholderText="MM/DD/YYYY"
                                            selected={fromDateFilterToDate}
                                            onChange={
                                                handleFromDateFilterChange
                                            }
                                            maxDate={
                                                toDateFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                    </div>
                                    <DatePicker
                                        label="To DOS"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateFilterToDate}
                                        onChange={handleToDateFilterChange}
                                        minDate={fromDateFilterToDate}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    <TableFiltersDisplay
                        wrapperClass="mt-[--spacing-items]"
                        localStorageKey="coded_client_notes_filters"
                        appliedFilters={[
                            {
                                key: "type[]",
                                values: noteTypeFilter.map(
                                    (type) => NoteLabels[type]
                                ),
                            },
                            {
                                key: "from_date",
                                values: fromDateFilter || "",
                            },
                            {
                                key: "to_date",
                                values: toDateFilter || "",
                            },
                            {
                                key: "insurance_provider_ids[]",
                                label: "Insurance",
                                preserveValues: true,
                                values:
                                    insuranceOptions
                                        .filter((provider) =>
                                            insuranceProviderIdsFilter.includes(
                                                provider.insurance_provider_id
                                            )
                                        )
                                        .map((provider) =>
                                            removeEnumUnderscore(provider.alias)
                                        ) || [],
                            },
                        ]}
                    />
                </Card>

                {isLoading ? <Loader /> : null}

                {error ? (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error.response?.data.message ||
                            "Cannot display coded client notes at this time"
                        }
                    />
                ) : null}

                {isSuccess && groupedNotes.length === 0 ? (
                    <ListState
                        isError={false}
                        stateHelperText="Coded notes without claims will appear here"
                        emptyMessage="No coded notes found"
                    />
                ) : null}

                {isSuccess && groupedNotes.length > 0 ? (
                    <div className="flex flex-col gap-[--spacing-items]">
                        {selectionValidationBanner ? (
                            <Card>
                                <div className="text-caption text-danger">
                                    {selectionValidationBanner}
                                </div>
                            </Card>
                        ) : null}
                        <Card>
                            <Table
                                data={clientTableRows}
                                columns={clientColumns}
                                className={COMPACT_TABLE_CLASS}
                                handleRowClick={(row) => {
                                    row.toggleExpanded();
                                    row.getToggleExpandedHandler();
                                }}
                                hasPagination={totalClientCount > limitFilter}
                                expandedRows={expandedRows}
                                setExpandedRows={setExpandedRows}
                                renderRowSubComponent={renderRowSubComponent}
                                pagination={
                                    <Pagination
                                        totalCount={totalClientCount}
                                        totalPages={totalPages}
                                        currentPage={currentPage}
                                        onPageChange={handlePageChange}
                                        limit={limitFilter}
                                        onLimitChange={handleLimitChange}
                                    />
                                }
                            />
                        </Card>
                    </div>
                ) : null}
            </div>
        </>
    );
}
